/**
 * StackedColumn Component
 *
 * A container that vertically stacks two TaskColumns in a single visual column.
 * Used for compact layout where related statuses share one column.
 *
 * @module components/tasks/StackedColumn
 */

import { memo } from 'react';
import { TaskColumnSimple } from './TaskColumn.jsx';

/**
 * StackedColumn Component
 *
 * @param {object} props - Component props
 * @param {string} props.topStatus - Status for top column
 * @param {string} props.bottomStatus - Status for bottom column
 * @param {object} props.tasksByStatus - Tasks grouped by status
 * @param {function} [props.onTaskClick] - Handler when a task is clicked
 * @param {function} [props.onAddTask] - Handler when add task button is clicked
 * @param {Set} [props.highlightedTaskIds] - Set of task IDs to highlight
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Stacked column element
 */
const StackedColumn = memo(function StackedColumn({
  topStatus,
  bottomStatus,
  tasksByStatus = {},
  onTaskClick,
  onAddTask,
  highlightedTaskIds = new Set(),
  className = ''
}) {
  return (
    <div className={`flex flex-col gap-2 h-full min-h-0 ${className}`}>
      {/* Top Column - takes up half the height */}
      <div className="flex-1 min-h-0">
        <TaskColumnSimple
          status={topStatus}
          tasks={tasksByStatus[topStatus] || []}
          onTaskClick={onTaskClick}
          onAddTask={onAddTask}
          highlightedTaskIds={highlightedTaskIds}
          className="h-full"
        />
      </div>

      {/* Bottom Column - takes up half the height */}
      <div className="flex-1 min-h-0">
        <TaskColumnSimple
          status={bottomStatus}
          tasks={tasksByStatus[bottomStatus] || []}
          onTaskClick={onTaskClick}
          onAddTask={onAddTask}
          highlightedTaskIds={highlightedTaskIds}
          className="h-full"
        />
      </div>
    </div>
  );
});

export default StackedColumn;
